/**
 * Header - ClariCommand editorial topbar (dark ink bg, paper text).
 *
 * Adapted from the Starter Kit Header: the avatar cluster renders the operator's NAME and EMAIL
 * (the rev3 multi-operator identity requirement) rather than name + role. An optional role pill
 * sits in the meta strip. Pure presentational; identity comes from the caller (useEntraIdentity).
 */
import type { ReactNode } from 'react';

export interface HeaderBrand {
  mark: string;
  name: string;
  nameEm?: string;
}

export interface HeaderMetaItem {
  label: string;
  /** Plain text, or a control (the CLIENT picker renders a <select> here). */
  value: ReactNode;
}

export interface HeaderUser {
  name: string;
  email: string;
  initials: string;
}

export interface HeaderProps {
  brand: HeaderBrand;
  meta?: HeaderMetaItem[] | undefined;
  role?: string | undefined;
  user?: HeaderUser | undefined;
}

export function Header({ brand, meta, role, user }: HeaderProps) {
  return (
    <header className="topbar" role="banner">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0, flexShrink: 0 }}>
        <span className="brand-mark">{brand.mark}</span>
        <span className="brand-name" style={{ whiteSpace: 'nowrap' }}>
          {brand.name}
          {brand.nameEm ? <em>{brand.nameEm}</em> : null}
        </span>
      </div>

      <span className="sep" style={{ flexShrink: 0 }} />

      {meta && meta.length > 0 && (
        <div className="tb-meta" style={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
          {meta.map((m) => (
            <span key={m.label}>
              <b>{m.label}</b> {m.value}
            </span>
          ))}
        </div>
      )}

      <span style={{ marginLeft: 'auto' }} />

      {role && (
        <span className="tb-pill" style={{ flexShrink: 0 }}>
          <b>{role}</b>
        </span>
      )}

      {user && (
        <div className="tb-avatar" title={user.email}>
          <span className="av-circle">{user.initials}</span>
          <div style={{ minWidth: 0 }}>
            <div className="nm" style={{ whiteSpace: 'nowrap' }}>{user.name}</div>
            <div className="ro" style={{ whiteSpace: 'nowrap', textTransform: 'none', letterSpacing: 0 }}>
              {user.email}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
